import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { eventEmitter } from "../../../utils/eventEmitter";

// One chip per statement Superdoc just committed to memory ("Remembered: you
// prefer the metabolic panel"). Undo dispatches `memory_undo` for that entry
// and flips the chip to "Forgotten" locally — the entry is retired server-side,
// but the chip stays in the thread so the clinician can see what was (and is
// no longer) remembered. Undo hides once the backend's undo window has passed,
// once the entry is already undone, or on a frozen card from an earlier turn.
//
// The mirror image: an entry the clinician asked to forget in chat arrives
// with `forgotten` and renders "Forgotten:" with an Undo that dispatches
// `memory_restore` and flips it back to "Remembered:" (`restored`).
export interface MemorySavedEntry {
  entry_id: string;
  statement: string;
  scope: "user" | "user_patient" | "clinic";
  category?: string;
  undo_until?: string | null;
  undone?: boolean;
  forgotten?: boolean;
  restore_until?: string | null;
  restored?: boolean;
  // A later memory superseded this one: the chip reads "Replaced" and has no
  // Undo. Set by the server on reload; in the session, a new chip that
  // carries `replaces_entry_id` flips the earlier one through the emitter.
  replaced?: boolean;
  replaced_by?: string | null;
  replaces_entry_id?: string | null;
}

export interface MemorySavedData {
  entries: MemorySavedEntry[];
  anchor_message_id?: string | null;
}

interface MemorySavedProps {
  data: MemorySavedData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // This card belongs to an earlier turn — render it read-only.
  frozen?: boolean;
}

const BookmarkIcon = ({ muted }: { muted?: boolean }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, display: "block" }}>
    <path
      d="M4 2.5h8a.5.5 0 0 1 .5.5v10.5L8 10.5l-4.5 3V3a.5.5 0 0 1 .5-.5Z"
      fill={muted ? "#F3F4F6" : "#EEF2FF"}
      stroke={muted ? "#9CA3AF" : "#4F46E5"}
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

const undoExpired = (undoUntil?: string | null): boolean => {
  if (!undoUntil) return false;
  const ts = Date.parse(undoUntil);
  return !Number.isNaN(ts) && ts < Date.now();
};

const REPLACED_EVENT = "memoryReplaced";
// A chat "forget" lands as a new "Forgotten" chip. The "Remembered" chip from
// the turn that saved the entry reads Forgotten too from then on, and reads
// Remembered again once the Forgotten chip's Undo restores it; the server
// persists the same flips for a reload.
const FORGOTTEN_EVENT = "memoryForgotten";
const RESTORED_EVENT = "memoryRestored";

const MemorySaved: React.FC<MemorySavedProps> = ({ data, onCustomAction, disabled, frozen }) => {
  const rawEntries = data?.entries;
  // Memoised on the data, not rebuilt per render: both effects below key on
  // it, and a fresh array every render would re-emit — and, for a chip that
  // carries both a memory and the one it replaced, re-render — forever.
  const entries = useMemo(
    () => (rawEntries ?? []).filter((e) => !!e?.entry_id && !!e?.statement?.trim()),
    [rawEntries],
  );
  // Entries toggled in this session: undone "Remembered" chips and restored
  // "Forgotten" chips (one set — an entry is only ever one or the other).
  const [toggledIds, setToggledIds] = useState<Set<string>>(() => new Set());
  // Entries a later chip in this session replaced.
  const [replacedIds, setReplacedIds] = useState<Set<string>>(() => new Set());
  // What a later "Forgotten" chip did to a "Remembered" chip's entry in this
  // session: forgot it, or restored it again. A restore also overrides a
  // persisted `undone` (a reload after the forget), so the chip revives
  // without another reload.
  const [overrides, setOverrides] = useState<Map<string, "forgotten" | "restored">>(() => new Map());

  useEffect(() => {
    const idOf = (...args: unknown[]) => (args[0] as { entry_id?: string })?.entry_id;
    const onReplaced = (...args: unknown[]) => {
      const id = idOf(...args);
      if (id && entries.some((e) => e.entry_id === id)) {
        setReplacedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
      }
    };
    // Only a "Remembered" chip follows a forget or restore elsewhere; the
    // Forgotten chip that announced it renders its own state.
    const override = (state: "forgotten" | "restored") => {
      return (...args: unknown[]) => {
        const id = idOf(...args);
        if (id && entries.some((e) => e.entry_id === id && !e.forgotten)) {
          setOverrides((prev) => (prev.get(id) === state ? prev : new Map(prev).set(id, state)));
        }
      };
    };
    const onForgotten = override("forgotten");
    const onRestored = override("restored");
    eventEmitter.on(REPLACED_EVENT, onReplaced);
    eventEmitter.on(FORGOTTEN_EVENT, onForgotten);
    eventEmitter.on(RESTORED_EVENT, onRestored);
    return () => {
      eventEmitter.off(REPLACED_EVENT, onReplaced);
      eventEmitter.off(FORGOTTEN_EVENT, onForgotten);
      eventEmitter.off(RESTORED_EVENT, onRestored);
    };
  }, [entries]);

  // Announce what this chip replaced or forgot, once, so the earlier chip
  // updates in place.
  useEffect(() => {
    for (const e of entries) {
      if (e.replaces_entry_id) eventEmitter.emit(REPLACED_EVENT, { entry_id: e.replaces_entry_id });
      if (e.forgotten && !e.restored) eventEmitter.emit(FORGOTTEN_EVENT, { entry_id: e.entry_id });
    }
  }, [entries]);

  if (!entries.length) return null;

  const undo = (entry: MemorySavedEntry) => {
    if (disabled || frozen) return;
    onCustomAction({
      type: entry.forgotten ? "memory_restore" : "memory_undo",
      payload: { entry_id: entry.entry_id },
    });
    setToggledIds((prev) => new Set(prev).add(entry.entry_id));
    if (entry.forgotten) eventEmitter.emit(RESTORED_EVENT, { entry_id: entry.entry_id });
  };

  return (
    <div style={styles.container}>
      {entries.map((entry) => {
        const toggled = toggledIds.has(entry.entry_id);
        const replaced = !entry.forgotten && (!!entry.replaced || replacedIds.has(entry.entry_id));
        const override = entry.forgotten ? undefined : overrides.get(entry.entry_id);
        // What the chip says now: a "Remembered" chip reads Forgotten once
        // undone (here, or by a later forget) and Replaced once superseded,
        // and Remembered again once a later chip restored it; a "Forgotten"
        // chip reads Remembered once restored.
        const undone = entry.forgotten
          ? !(entry.restored || toggled)
          : toggled || override === "forgotten" || (!!entry.undone && override !== "restored");
        const settled = replaced || (entry.forgotten ? !!entry.restored || toggled : undone);
        const muted = undone || replaced;
        const window = entry.forgotten ? entry.restore_until : entry.undo_until;
        // No window from the server means nothing to undo here: show no
        // button rather than one the backend would refuse.
        const canUndo = !settled && !frozen && !!window && !undoExpired(window);
        const label = replaced ? "Replaced:" : undone ? "Forgotten:" : "Remembered:";
        return (
          <div key={entry.entry_id} style={{ ...styles.chip, ...(muted ? styles.chipUndone : {}) }}>
            <BookmarkIcon muted={muted} />
            <span style={{ ...styles.text, ...(muted ? styles.textUndone : {}) }}>
              <span style={styles.label}>{label}</span> {entry.statement}
            </span>
            {canUndo && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => undo(entry)}
                style={{ ...styles.undo, ...(disabled ? styles.undoDisabled : {}) }}
              >
                Undo
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    marginTop: "8px",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  chip: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: "6px",
    padding: "5px 9px",
    borderRadius: "999px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
    alignSelf: "flex-start",
    maxWidth: "100%",
  },
  chipUndone: { border: "1px solid #E5E7EB", backgroundColor: "#F9FAFB" },
  text: { fontSize: "12px", color: "#374151", lineHeight: 1.4, minWidth: 0 },
  textUndone: { color: "#9CA3AF", textDecoration: "line-through" },
  label: { fontWeight: 600, color: "#4338CA" },
  undo: {
    flexShrink: 0,
    padding: 0,
    border: "none",
    background: "none",
    fontSize: "12px",
    fontWeight: 500,
    color: "#4F46E5",
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  undoDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default MemorySaved;
